import { useState, ReactNode } from 'react';

export interface AccordionColumn<T> {
  field: keyof T | string;
  label?: string;
  render?: (value: any, row: T) => ReactNode;
  className?: string;
}

export interface MobileAccordionTableProps<T> {
  data: T[];
  /** 접힌 상태에서 보여줄 컬럼 (2-3개 권장) */
  summaryColumns: AccordionColumn<T>[];
  /** 펼쳤을 때 보여줄 상세 컬럼 */
  detailColumns: AccordionColumn<T>[];
  /** 행 클릭 핸들러 (상세 페이지 이동 등) */
  onRowClick?: (row: T) => void;
  /** 행별 액션 버튼 */
  actions?: (row: T) => ReactNode;
  /** 고유 키 필드 */
  keyField?: keyof T;
  /** 빈 데이터 메시지 */
  emptyMessage?: string;
  /** 로딩 상태 */
  loading?: boolean;
  /** 선택 기능 활성화 */
  selectable?: boolean;
  /** 선택된 행 키 목록 */
  selectedKeys?: Set<string | number>;
  /** 선택 변경 핸들러 */
  onSelectionChange?: (selectedKeys: Set<string | number>, selectedRows: T[]) => void;
}

/**
 * @author ellie
 * @date 2026.02.25
 * @description 모바일용 아코디언 테이블 컴포넌트
 */
const MobileAccordionTable = <T extends Record<string, any>>({
  data,
  summaryColumns,
  detailColumns,
  onRowClick,
  actions,
  keyField = 'id' as keyof T,
  emptyMessage = '데이터가 없습니다.',
  loading = false,
  selectable = false,
  selectedKeys: externalSelectedKeys,
  onSelectionChange,
}: MobileAccordionTableProps<T>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Set<string | number>>(new Set());
  
  // 외부 제어 또는 내부 상태 사용
  const selectedKeys = externalSelectedKeys ?? internalSelectedKeys;
  
  const toggleSelection = (key: string | number, row: T, e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    const newSelectedKeys = new Set(selectedKeys);
    if (newSelectedKeys.has(key)) {
      newSelectedKeys.delete(key);
    } else {
      newSelectedKeys.add(key);
    }
    
    if (onSelectionChange) {
      const selectedRows = data.filter(r => newSelectedKeys.has(r[keyField]));
      onSelectionChange(newSelectedKeys, selectedRows);
    } else {
      setInternalSelectedKeys(newSelectedKeys);
    }
  };
  
  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedKeys = e.target.checked 
      ? new Set(data.map(row => row[keyField] as string | number))
      : new Set<string | number>();
    
    if (onSelectionChange) {
      const selectedRows = e.target.checked ? [...data] : [];
      onSelectionChange(newSelectedKeys, selectedRows);
    } else {
      setInternalSelectedKeys(newSelectedKeys);
    }
  };
  
  const isAllSelected = data.length > 0 && selectedKeys.size === data.length;

  const toggleRow = (key: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getValue = (row: T, field: string) => {
    const keys = field.split('.');
    let value: any = row;
    for (const key of keys) {
      value = value?.[key];
    }
    return value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 bg-white">
      {/* 전체선택 헤더 (selectable일 때만) */}
      {selectable && data.length > 0 && (
        <div className="!flex !items-center !px-3 !py-2 !bg-gray-50 !border-b">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            className="!w-4 !h-4 !rounded !border-gray-300 !text-blue-600 focus:!ring-blue-500"
          />
          <span className="!ml-2 !text-xs !text-gray-600">
            전체선택 ({selectedKeys.size}/{data.length})
          </span>
        </div>
      )}
      {data.map((row, index) => {
        const key = row[keyField] ?? index;
        const isExpanded = expandedRows.has(key);
        const isSelected = selectedKeys.has(key);

        return (
          <div key={key} className={`${isExpanded ? 'bg-blue-50 border-l-2 border-l-blue-500' : isSelected ? 'bg-blue-50/30' : 'bg-white'}`}>
            {/* 접힌 상태 - 요약 행 */}
            <div
              className={`flex items-center px-3 py-2 gap-2 cursor-pointer ${isExpanded ? '' : 'active:bg-gray-50'}`}
              onClick={(e) => toggleRow(key, e)}
            >
              {/* 체크박스 (selectable일 때만) */}
              {selectable && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => toggleSelection(key, row, e)}
                  onClick={(e) => e.stopPropagation()}
                  className="!flex-shrink-0 !w-4 !h-4 !rounded !border-gray-300 !text-blue-600 focus:!ring-blue-500"
                />
              )}
              
              {/* 요약 컬럼들 */}
              <div className="flex-1 flex items-center gap-2 min-w-0">
                {summaryColumns.map((col, colIndex) => {
                  const value = getValue(row, col.field as string);
                  return (
                    <div
                      key={colIndex}
                      className={`truncate ${col.className || ''} ${colIndex === 0 ? 'flex-shrink-0' : 'flex-1'}`}
                    >
                      {col.render ? col.render(value, row) : (
                        <span className="text-sm text-gray-900">{value ?? '-'}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 상세 버튼 */}
              {onRowClick && (
                <button
                  className="flex-shrink-0 px-2 py-0.5 text-xs text-blue-600 bg-blue-50 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick(row);
                  }}
                >
                  상세
                </button>
              )}
            </div>

            {/* 펼친 상태 - 상세 정보 */}
            {isExpanded && (
              <div className="px-3 pb-3 pt-2 bg-blue-50/50">
                <div className="space-y-1.5">
                  {detailColumns.map((col, colIndex) => {
                    const value = getValue(row, col.field as string);
                    return (
                      <div key={colIndex} className="flex text-xs">
                        <span className="w-16 flex-shrink-0 text-gray-500">{col.label}</span>
                        <span className="flex-1 text-gray-900">
                          {col.render ? col.render(value, row) : (value ?? '-')}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* 액션 버튼들 */}
                {actions && (
                  <div className="mt-2.5 pt-2 border-t border-blue-100 flex gap-2">
                    {actions(row)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MobileAccordionTable;
